import { Grid, Typography, useTheme } from '@mui/material';
import dayjs from 'dayjs';
import useTranslation from 'hooks/useTranslation/useTranslation';
import React, { useMemo } from 'react';
import useSnackbarContext from 'v2/components/Snackbar/useSnackbarContext';
import useCurrentUser from 'v2/hooks/useCurrentUser/useCurrentUser';
import useDashboardBatteries from 'v2/hooks/useDashboardBatteries/useDashboardBatteries';
import useDashboardCards from 'v2/hooks/useDashboardCards/useDashboardCards';
import useDashboardTodo from 'v2/hooks/useDashboardTodo/useDashboardTodo';
import useDashboardUsers from 'v2/hooks/useDashboardUsers/useDashboardUsers';
import useRecentAccessEvents from 'v2/hooks/useRecentAccessEvents/useRecentAccessEvents';
import useWeeklyAccessEvents from 'v2/hooks/useWeeklyAccessEvents/useWeeklyAccessEvents';
import PageLayout from 'v2/layout/PageLayout/PageLayout';
import Card from 'v2/pages/Dashboard/cards/Card';
import DeviceOpeningsPerDay from 'v2/pages/Dashboard/charts/DeviceOpeningsPerDay';
import HourlyAccessEvents from 'v2/pages/Dashboard/charts/HourlyAccessEvents';
import RankedDevicesChart from 'v2/pages/Dashboard/charts/RankedDevicesChart';
import useBatteryRows from 'v2/pages/Dashboard/hooks/useBatteryRows';
import useCardsRows from 'v2/pages/Dashboard/hooks/useCardsRows';
import useTodoRows from 'v2/pages/Dashboard/hooks/useTodoRows';
import useUsersRows from 'v2/pages/Dashboard/hooks/useUsersRows';
import { formatTranslation } from 'v2/utils/formatTranslation';

const MORNING_START_HOUR = 5;
const AFTERNOON_START_HOUR = 12;
const EVENING_START_HOUR = 18;

const Dashboard: React.FC = () => {
  const { translation } = useTranslation();
  const theme = useTheme();
  const { showSnackbar } = useSnackbarContext();
  const handleFetchError = (errorMessage: string) => showSnackbar(errorMessage, { severity: 'error' });
  const { currentUser } = useCurrentUser({ translation, handleFetchError });
  const {
    totalUsers,
    totalUserGroups,
    totalDevices,
    totalDeviceGroups,
    usersWithoutGroup,
    userGroupsWithoutSchedule,
    userGroupsWithoutDevices,
    userGroupsWithoutUsers,
    deviceGroupsWithoutDevices,
    deviceGroupsWithoutUserGroups,
    loadingDashboardTodo
  } = useDashboardTodo({ handleFetchError });
  const { batteriesEmpty, batteriesCritical, batteriesLow, batteriesUnknown, totalDoors, loadingDashboardBatteries } =
    useDashboardBatteries({ handleFetchError });
  const { cardsExpired, cardsRightsChanged, cardsBlocked, cardsExpiringSoon, totalCards, loadingDashboardCards } =
    useDashboardCards({ handleFetchError });
  const { usersConfigDisconnected, usersDisconnectedWithoutCard, loadingDashboardUsers } = useDashboardUsers({
    handleFetchError
  });
  const {
    dailyOpenings,
    mostAccessedDevices,
    mostDeniedDevices,
    averageOpeningsPerDay,
    peakDay,
    loading: loadingWeeklyAccessEvents,
    error: weeklyAccessEventsError
  } = useWeeklyAccessEvents({ handleFetchError, translation });
  const { accessEventBuckets, loading: loadingRecentAccessEvents } = useRecentAccessEvents({
    handleFetchError,
    translation
  });

  const userName = currentUser?.name;

  const todoRows = useTodoRows({
    totalUsers,
    totalUserGroups,
    totalDevices,
    totalDeviceGroups,
    usersWithoutGroup,
    userGroupsWithoutSchedule,
    userGroupsWithoutDevices,
    userGroupsWithoutUsers,
    deviceGroupsWithoutDevices,
    deviceGroupsWithoutUserGroups
  });

  const batteryRows = useBatteryRows({ batteriesEmpty, batteriesCritical, batteriesLow, batteriesUnknown });

  const cardsRows = useCardsRows({ cardsExpired, cardsRightsChanged, cardsBlocked, cardsExpiringSoon });

  const usersRows = useUsersRows({ usersConfigDisconnected, usersDisconnectedWithoutCard });

  const greeting = useMemo(() => {
    const hour = dayjs().hour();

    let greetingTemplate = translation.good_evening;
    if (hour >= MORNING_START_HOUR && hour < AFTERNOON_START_HOUR) {
      greetingTemplate = translation.good_morning;
    } else if (hour >= AFTERNOON_START_HOUR && hour < EVENING_START_HOUR) {
      greetingTemplate = translation.good_afternoon;
    }

    return formatTranslation(greetingTemplate, { userName: userName ?? '' });
  }, [translation.good_morning, translation.good_afternoon, translation.good_evening, userName]);

  const batteriesEmptyState =
    totalDoors === 0
      ? { title: translation.dashboard_empty_devices_title, content: translation.dashboard_empty_devices_content }
      : undefined;

  const cardsEmptyState =
    totalCards === 0
      ? { title: translation.dashboard_empty_cards_title, content: translation.dashboard_empty_cards_content }
      : undefined;

  const usersEmptyState =
    totalUsers === 0
      ? { title: translation.dashboard_empty_users_title, content: translation.dashboard_empty_users_content }
      : undefined;

  return (
    <PageLayout>
      <Typography variant="h3">{greeting}</Typography>
      <Grid container spacing={3} sx={{ paddingTop: 3, width: '100%' }}>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <Card header={translation.to_do} isLoading={loadingDashboardTodo} rows={todoRows} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <Card
            header={translation.batteries}
            isLoading={loadingDashboardBatteries}
            rows={batteryRows}
            emptyState={batteriesEmptyState}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <Card
            header={translation.cards}
            isLoading={loadingDashboardCards}
            rows={cardsRows}
            emptyState={cardsEmptyState}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <Card
            header={translation.users}
            isLoading={loadingDashboardUsers}
            rows={usersRows}
            emptyState={usersEmptyState}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <DeviceOpeningsPerDay
            dailyOpenings={dailyOpenings}
            averageOpeningsPerDay={averageOpeningsPerDay}
            peakDay={peakDay}
            isLoading={loadingWeeklyAccessEvents}
            hasError={weeklyAccessEventsError}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <HourlyAccessEvents accessEventBuckets={accessEventBuckets} isLoading={loadingRecentAccessEvents} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <RankedDevicesChart
            devices={mostAccessedDevices}
            color={theme.palette.primary.main}
            title={translation.most_accessed_devices}
            subtitle={translation.most_accessed_devices_this_week}
            emptyStateContent={translation.dashboard_empty_most_accessed_content}
            isLoading={loadingWeeklyAccessEvents}
            hasError={weeklyAccessEventsError}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <RankedDevicesChart
            devices={mostDeniedDevices}
            color={theme.palette.secondary.light}
            title={translation.most_denied_devices}
            subtitle={translation.most_denied_devices_this_week}
            emptyStateContent={translation.dashboard_empty_most_denied_content}
            isLoading={loadingWeeklyAccessEvents}
            hasError={weeklyAccessEventsError}
          />
        </Grid>
      </Grid>
    </PageLayout>
  );
};

export default Dashboard;
